import mixpanel, { Dict } from 'mixpanel-browser';

import { CONSTANT_VARIABLES } from 'src/constants/constants-variables';

import { storedUserRole, ANONYMOUS_ROLE } from './check-user-is-admin';

import { captureAndStripUrlSecrets } from './url-secrets';

/**
 * Before `mixpanel.init` below, and unconditionally — the token must be out of
 * the URL whether or not analytics is configured. `init` tracks a page view
 * immediately (`track_pageview: 'url-with-path'`) and can start a recording
 * whose rrweb Meta event carries the href; both would carry a password-reset
 * token if it were still there. NAT-2172 item 4, the gate on NAT-2171.
 */
captureAndStripUrlSecrets();

export const ORDER_FLOW_STARTED_AT_KEY = 'order_flow_started_at';

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN as string | undefined;
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT as string | undefined;
const IS_PRODUCTION = ENVIRONMENT === CONSTANT_VARIABLES.PRODUCTION;

const PERFORMANCE_CATEGORY_PREFIX = 'performance';

const COOKIE_SCRIPT_EVENTS = [
  'CookieScriptAcceptAll',
  'CookieScriptAccept',
  'CookieScriptReject',
] as const;

type CookieScriptState = { categories?: unknown };

type CookieScriptInstance = {
  currentState?: () => CookieScriptState | undefined;
};

declare global {
  interface Window {
    CookieScript?: { instance?: CookieScriptInstance };
  }
}

/**
 * The super-properties every event carries. Defined once because init and
 * `reset()` both set them, and a third property added at init would otherwise be
 * silently dropped the first time anyone signs out.
 */
const baseSuperProperties = (role: string) => ({
  environment: ENVIRONMENT,
  user_role: role,
});

const isAdminRoute = () => {
  if (typeof window === 'undefined') return false;
  const { pathname } = window.location;
  return pathname === '/admin' || pathname.startsWith('/admin/');
};

const hasPerformanceConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  const state = window.CookieScript?.instance?.currentState?.();
  const categories = state?.categories;
  if (!Array.isArray(categories)) return false;
  return categories.some(
    (c) => typeof c === 'string' && c.toLowerCase().startsWith(PERFORMANCE_CATEGORY_PREFIX),
  );
};

/**
 * Session recording is started by the SDK itself, so unlike `track` and
 * `identify` it never passes through this module's guards — the admin check
 * that keeps admin activity out of the event stream does not apply to it.
 * Without this, an operator working in /admin would be recorded (NAT-2171).
 *
 * Agents are recorded, deliberately. They use the same shop the customers do,
 * ordering on a client's behalf, so their sessions answer the same questions a
 * customer's does — /agent/* is inside the boundary, only /admin is outside it.
 *
 * Called on consent changes and on every route change, because this is a SPA:
 * an admin who opens the catalogue and then navigates into /admin must stop
 * being recorded at that moment, not at the next full page load.
 */
export const syncSessionRecording = () => {
  if (!MIXPANEL_TOKEN) return;
  if (typeof window === 'undefined') return;
  try {
    if (isAdminRoute() || mixpanel.has_opted_out_tracking()) {
      mixpanel.stop_session_recording();
    } else {
      mixpanel.start_session_recording();
    }
  } catch {
    // Recording is never worth an exception in the caller's render path.
  }
};

const syncConsentFromCookieScript = () => {
  if (!MIXPANEL_TOKEN) return;
  if (hasPerformanceConsent()) {
    mixpanel.opt_in_tracking();
  } else {
    mixpanel.opt_out_tracking();
  }
  // Consent going away has to stop an in-flight recording, not merely prevent
  // the next one.
  syncSessionRecording();
};

if (MIXPANEL_TOKEN) {
  mixpanel.init(MIXPANEL_TOKEN, {
    api_host: 'https://api-eu.mixpanel.com',
    persistence: 'localStorage',
    track_pageview: 'url-with-path',
    autocapture: false,

    /**
     * Session Replay (NAT-2171). Aggregates told us half our customers are on a
     * phone and that browsing outnumbers ordering four to one; they cannot say
     * why. Replay can. Real traffic is under a session a day, so recording all
     * of it still yields few enough to actually watch.
     *
     * Sampling stays at 0 and recording is started explicitly by
     * syncSessionRecording() instead. Sampling would have the SDK start on its
     * own at init — including on an admin route, before anything of ours could
     * intervene. start_session_recording() forces past the sample check while
     * still honouring opt-out, so this gives us the admin exclusion with no
     * window in which an operator's session is already being recorded.
     *
     * It records layout, clicks, scrolls and hesitation — not content. The two
     * masking flags are Mixpanel's own defaults, pinned explicitly on purpose:
     * this is the one setting where an SDK default quietly changing would
     * expose customers' delivery addresses and order contents, and a default is
     * a promise someone else can revise. Password, email, tel and hidden inputs
     * are masked by the SDK regardless.
     *
     * Do not add `record_unmask_text_selector` here without its own privacy
     * review. Replays attach to distinct_id, so they are pseudonymous personal
     * data, not anonymous — masked is not the same as unidentifiable.
     */
    record_sessions_percent: 0,
    record_mask_all_text: true,
    record_mask_all_inputs: true,

    /**
     * Console capture defaults to ON, and it is not covered by the masking
     * above — rrweb serialises console arguments directly. A single
     * `console.error(axiosError)` would put a response body, and with it an
     * address or an order, into the replay in clear text. Everything the
     * masking flags protect, this would have leaked around.
     */
    record_console: false,

    /**
     * Closes the one path by which a recording can survive into an admin
     * document. `init()` ends with a non-forced start check, and while
     * `record_sessions_percent: 0` skips the sampling branch, the fallback
     * still resumes any recording the IndexedDB registry holds for this tab —
     * and tab ids outlive a document load. So an operator who goes from the
     * shop to an /admin URL in the same tab would have their recording resumed
     * by the SDK, asynchronously, after our route guard has already run; the
     * guard's stop is a no-op while the recorder is still loading.
     *
     * `disable_persistence` makes `shouldLoadRecorder()` refuse immediately, so
     * the resume cannot happen at all. Only for /admin: agents need persistence
     * for their distinct_id, since their events are tracked even though their
     * screens are not.
     */
    disable_persistence: isAdminRoute(),

    debug: !IS_PRODUCTION,
    opt_out_tracking_by_default: true,
  });
  /**
   * The role comes from storage, NOT hardcoded to `anonymous`.
   *
   * Super-properties persist (`persistence: 'localStorage'`), so the correct
   * role from the previous session is already here — and registering
   * `anonymous` over it mislabelled every event that followed, until the auth
   * effect in `auth-provider.tsx` caught up on a later commit. That is where
   * the 26 anonymous page views of /catalog came from: a route you cannot
   * reach signed out.
   *
   * Note what this does NOT cover. The SDK's own init-time page view fires
   * INSIDE `mixpanel.init()` above — `autocapture.init()` →
   * `initPageviewTracking()` → `track_pageview()`, all synchronous — so it
   * precedes this line and always carried the persisted role, before and after
   * this change. Only the events after init were ever mislabelled.
   *
   * The sign-in event is fixed separately, in the login callbacks themselves:
   * it fires before this effect chain can run at all. NAT-2318.
   */
  mixpanel.register(baseSuperProperties(storedUserRole()));

  if (typeof window !== 'undefined') {
    syncConsentFromCookieScript();
    COOKIE_SCRIPT_EVENTS.forEach((eventName) => {
      window.addEventListener(eventName, syncConsentFromCookieScript);
    });
  }
}

const analytics = {
  identify: (userId: string, traits?: Dict) => {
    if (!MIXPANEL_TOKEN) return;
    if (isAdminRoute()) return;
    if (mixpanel.has_opted_out_tracking()) return;
    mixpanel.identify(userId);
    if (traits) mixpanel.people.set(traits);
  },

  track: (event: string, properties?: Dict) => {
    if (!MIXPANEL_TOKEN) return;
    if (isAdminRoute()) return;
    if (mixpanel.has_opted_out_tracking()) return;
    mixpanel.track(event, properties);
  },

  reset: () => {
    sessionStorage.removeItem(ORDER_FLOW_STARTED_AT_KEY);
    if (!MIXPANEL_TOKEN) return;
    mixpanel.reset();
    /**
     * `reset()` clears super-properties along with the distinct_id — verified
     * in the SDK, `mixpanel-core.js` reset() → `persistence.clear()` — so
     * without this the events after a sign-out carry no `user_role` at all.
     * That is an absent value, not `anonymous`, and a separate row on every
     * board that splits by role. Restate the baseline immediately. NAT-2318.
     */
    mixpanel.register(baseSuperProperties(ANONYMOUS_ROLE));
  },

  register: (properties: Dict) => {
    if (!MIXPANEL_TOKEN) return;
    mixpanel.register(properties);
  },

  optIn: () => {
    if (!MIXPANEL_TOKEN) return;
    mixpanel.opt_in_tracking();
  },

  optOut: () => {
    if (!MIXPANEL_TOKEN) return;
    mixpanel.opt_out_tracking();
  },
};

export default analytics;
